import React, { KeyboardEvent, MouseEvent } from "react";
import { checkIsYp } from "shared-auth";
import { isAfter } from "date-fns";
import cx from "classnames";
import {
  SocialMediaAccount,
  SocialMediaPlatformCode,
  UserTypes,
} from "shared-types";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { dataTestIds } from "data-testids";
import { Photo } from "../../../Atoms/Photo/Photo";
import "./Card.scss";
import SocialMediaPill, {
  SocialMediaPillSize,
  SocialMediaPillType,
} from "../../../Molecules/SocialMediaPill";

export interface ICardProps {
  /** Url for loading image */
  imageUrl: string;
  /**  Text to show the users full name */
  fullName: string;
  /** Text to show the membership type for the user */
  membershipType: string;
  /** Text to show the contract expiration days */
  membershipExp?: string | number;
  /** Text to show name of the user agent */
  performerAgent?: string;
  /** Email of the user agent */
  performerAgentEmail?: string;
  /** Text to display age of performer */
  performerAge?: string | number;
  /** Link for the user name to be clickable */
  linkUrl?: string;
  /** Alternate text for image */
  altText?: string;
  /** Handler for click event */
  handleClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** Card is active (selected) */
  active?: boolean;
  /** Optional css class for wrapper */
  className?: string;
  /** Optional id for wrapper */
  id?: string;
  /** Optional css modifier for cards style */
  cardSize?: "small" | "normal" | "tall";
  /** Flag to specify whether performer has audioclips or not */
  hasAudioclips?: boolean;
  /** Flag to specify whether performer has showreels or not */
  hasShowreels?: boolean;
  membershipExpiredDays?: string;
  socialMediaAccounts?: SocialMediaAccount[];
  onAudioClipClick?: () => void;
  onShowreelsClick?: () => void;
  userType?: UserTypes;
}

export const Card: React.FC<ICardProps> = ({
  className,
  imageUrl,
  altText,
  fullName,
  membershipType,
  linkUrl,
  active,
  handleClick,
  membershipExp,
  performerAge,
  performerAgent,
  cardSize = "normal",
  hasAudioclips,
  hasShowreels,
  membershipExpiredDays,
  socialMediaAccounts = [],
  onAudioClipClick,
  onShowreelsClick,
  performerAgentEmail,
  id,
  userType,
}) => {
  const isInstagramEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.InstagramIntegration
  );

  const isYoungPerformer = checkIsYp(userType);

  const showreelClickHandler = (e: MouseEvent<HTMLSpanElement>) => {
    if (onShowreelsClick) {
      e.stopPropagation();
      e.preventDefault();
      onShowreelsClick();
    }
  };

  const audioClipClickHandler = (e: MouseEvent<HTMLSpanElement>) => {
    if (onAudioClipClick) {
      e.stopPropagation();
      e.preventDefault();
      onAudioClipClick();
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      handleClick?.(e as unknown as MouseEvent<HTMLDivElement>);
    }
  };

  const handleCardNameClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (linkUrl) {
      e.stopPropagation();
    }
  };

  const cardClassNames = [
    "c-card",
    `c-card--${cardSize}`,
    { "c-card--active": active, "c-card--clickable": handleClick },
    className,
  ];

  const instagramAcc = socialMediaAccounts.find(
    (account) =>
      account.platformCode === SocialMediaPlatformCode.Instagram &&
      isAfter(new Date(account.tokenExpiresAt), new Date())
  );

  return (
    <div
      className={cx(cardClassNames)}
      onClick={handleClick}
      data-testid={dataTestIds.componentLibrary["Organisams.Cards.card"]}
      tabIndex={0}
      onKeyPress={handleKeyPress}
      id={id}
    >
      <div className="c-card__container">
        <div className="c-card__image-container">
          <Photo
            className="c-card__image"
            src={imageUrl}
            alt={altText}
            hasLoadingSpinner={true}
          />
          {membershipExp && (
            <div className="c-card__expiration-warning"> {membershipExp} </div>
          )}
          {membershipExpiredDays && (
            <div className="c-card__expired-warning">
              {membershipExpiredDays}
            </div>
          )}
        </div>
        <div className="c-card__content">
          <div className="c-card__name">
            {linkUrl ? (
              <a
                href={linkUrl}
                target="_blank"
                className="c-card__name-link"
                onClick={handleCardNameClick}
                rel="noreferrer"
              >
                {fullName.toUpperCase()}
              </a>
            ) : (
              <div>{fullName.toUpperCase()}</div>
            )}
          </div>
          <div className="c-card__member-type">{membershipType}</div>
          {performerAge && (
            <div className="c-card__performer-age">{performerAge}</div>
          )}
          {performerAgent && (
            <div className="c-card__performer-agent-wrapper">
              {performerAgentEmail ? (
                <a
                  className={cx("c-card__performer-agent", {
                    "g-alternative-link": performerAgentEmail,
                  })}
                  onClick={(event) => event.stopPropagation()}
                  href={`mailto:${performerAgentEmail}`}
                >
                  {performerAgent}
                </a>
              ) : (
                <div className={cx("c-card__performer-agent")}>
                  {performerAgent}
                </div>
              )}
            </div>
          )}
          <div className="c-card__icons-section">
            {hasShowreels && (
              <span
                className={cx("c-card__icon icon-showreel", {
                  "c-card__icon--clickable": onShowreelsClick,
                })}
                tabIndex={0}
                onClick={showreelClickHandler}
              />
            )}
            {hasAudioclips && (
              <span
                role="button"
                className={cx("c-card__icon icon-audio", {
                  "c-card__icon--clickable": onAudioClipClick,
                })}
                tabIndex={0}
                onClick={audioClipClickHandler}
              />
            )}
            {isInstagramEnabled && instagramAcc && !isYoungPerformer && (
              <SocialMediaPill
                variant={SocialMediaPlatformCode.Instagram}
                size={SocialMediaPillSize.Small}
                type={SocialMediaPillType.Clean}
                className="gap-0 p-0 ml-auto bg-color-neutral-three hover:bg-color-neutral-four"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(instagramAcc.profileUrl, "_blank");
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
